import React from "react";
import { Badge, Button } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { ROUTE_NAMES } from "@root/constants";
import { CommonTooltip } from "@root/components/common/tooltip/CommonTooltip";

/**
 * StatusBadge component for displaying document processing status
 *
 * @param status - The status string (e.g., "PROCESSING", "COMPLETED", "FAILED", "NOT_STARTED")
 * @param onManualProcess - Optional callback function for manual processing (used when status is "NOT_STARTED")
 * @param t - Translation function for internationalization
 * @param size - Size of the badge/button ("xs", "sm", "md", "lg")
 * @param variant - Whether to render as "badge" or "button"
 * @param customStyles - Optional custom colors to override default styling
 *
 * @example
 * // Basic usage
 * <StatusBadge status="PROCESSING" t={t} />
 *
 * // With manual process button
 * <StatusBadge
 *   status="NOT_STARTED"
 *   onManualProcess={() => handleProcess(file)}
 *   t={t}
 * />
 *
 * // Custom styling
 * <StatusBadge
 *   status="COMPLETED"
 *   t={t}
 *   customStyles={{ textColor: "#custom", bgColor: "#custom" }}
 * />
 */
interface StatusBadgeProps {
  status: string;
  onManualProcess?: () => void;
  size?: "xs" | "sm" | "md" | "lg";
  variant?: "badge" | "button";
  customStyles?: {
    textColor?: string;
    borderColor?: string;
    bgColor?: string;
  };
}

const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  onManualProcess,
  size = "xs",
  variant = "badge",
  customStyles,
}) => {
  const { t } = useTranslation("common");
  const { pathname } = useLocation();
  const isLegalReview = pathname === ROUTE_NAMES.LEGAL_REVIEW;
  const isPermitReview = pathname === ROUTE_NAMES.PERMIT_REVIEW;
  const isDrawingAnalysis = pathname === ROUTE_NAMES.DRAWING_ANALYSIS;
  const isMultiDoc = pathname === ROUTE_NAMES.CHECKLIST;

  const getStatusConfig = (status: string) => {
    if (isDrawingAnalysis) {
      return {
        text: t("upload_document.start_chat"),
        textColor: "#FFFFFF",
        borderColor: "#6648FF",
        bgColor: "#6648FF",
      };
    }
    switch (status) {
      case "PROCESSING":
        return {
          text: t("upload_document.processing"),
          textColor: "#2442A9",
          borderColor: "#2442A9",
          bgColor: "#2442A914",
        };
      case "COMPLETED":
        return {
          text:
            isLegalReview || isPermitReview
              ? t("upload_document.viewResult")
              : t("upload_document.completed"),
          textColor: "#FFFFFF",
          borderColor: "#0FABBE",
          bgColor: "#0FABBE",
        };
      case "FAILED":
        return {
          text: t("upload_document.failed"),
          textColor: "#FF0000",
          borderColor: "#FF0000",
          bgColor: "#FF000014",
        };
      case "NOT_STARTED":
        return {
          text: t("upload_document.start_processing"),
          textColor: "#FFFFFF",
          borderColor: "#6648FF",
          bgColor: "#6648FF",
        };
      default:
        return {
          text: status,
          textColor: "#6B7280",
          borderColor: "#6B7280",
          bgColor: "#F3F4F6",
        };
    }
  };

  const config = getStatusConfig(status);

  // Apply custom styles if provided
  const finalConfig = {
    ...config,
    ...customStyles,
  };

  // Determine if we should render as a button
  const shouldRenderAsButton = status === "NOT_STARTED" || variant === "button";

  // Get tooltip text based on button text
  const getTooltipLabel = () => {
    const viewResultText = t("upload_document.viewResult");
    const startProcessingText = t("upload_document.start_processing");

    if (finalConfig.text === viewResultText) {
      return t("tooltip.viewAnalysisReport");
    }
    if (finalConfig.text === startProcessingText) {
      return t("tooltip.readyToProcess");
    }
    return null;
  };

  const tooltipLabel = getTooltipLabel();

  if (true) {
    const button = (
      <Button
        size={size}
        borderRadius="6px"
        height={size === "xs" ? "34px" : size === "sm" ? "38px" : "42px"}
        px={3}
        py={0}
        fontSize={"14px"}
        fontWeight={500}
        bg={finalConfig.bgColor}
        textColor={finalConfig.textColor}
        border={`1px solid ${finalConfig.borderColor}`}
        onClick={onManualProcess}
        _hover={{
          bg:
            finalConfig.bgColor === "transparent"
              ? "#F3F4F6"
              : finalConfig.bgColor,
        }}
        disabled={!onManualProcess}
        cursor={onManualProcess ? "pointer" : "default"}
        width="140px"
        textTransform={"capitalize"}
      >
        {finalConfig.text}
      </Button>
    );

    if (tooltipLabel) {
      return <CommonTooltip label={tooltipLabel}>{button}</CommonTooltip>;
    }

    return button;
  }

  return (
    <Badge
      size={size}
      borderRadius="6px"
      height={size === "xs" ? "34px" : size === "sm" ? "38px" : "42px"}
      px={size === "xs" ? 3 : size === "sm" ? 4 : 5}
      fontSize={"14px"}
      display="flex"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
      fontWeight={500}
      textColor={finalConfig.textColor}
      border={`1px solid ${finalConfig.borderColor}`}
      bg={finalConfig.bgColor}
    >
      {finalConfig.text}
    </Badge>
  );
};

export default StatusBadge;
